import { _ } from 'core-js'
import Card from 'src/models/card/Card'
import MembershipVoucher from 'src/models/membership-type/MembershipVoucher'
import Model, { encoders, executeMutation, GqlError, ID, IsoDate, IsoDateTime, MODEL_LEVEL } from 'src/models/Model'
import Order from 'src/models/order/Order'
import User from 'src/models/user/User'
import IssuedVoucherPackage from 'src/models/voucher/IssuedVoucherPackage'
import VoucherType from 'src/models/voucher/VoucherType'

export const FILTER_BY_TYPES = {
  ANY: 'all',
  CARD: 'card',
  PACK: 'package',
  USER: 'user'
}
FILTER_BY_TYPES.ALL = Object.values(FILTER_BY_TYPES)

export const VOUCHER_STATES = {
  DRAFT: 'draft',
  VALID: 'valid',
  VOIDED: 'voided',
  EXPIRED: 'expired'
}
VOUCHER_STATES.ALL = _.values(VOUCHER_STATES)
VOUCHER_STATES.OPTIONS = [VOUCHER_STATES.VALID, VOUCHER_STATES.VOIDED]

export default class Voucher extends Model {
  static name = 'Voucher'

  static isExportable = true

  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Promotions]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      voucherType: {
        type: VoucherType,
        encode: encoders.hasOne,
        reference: true
      },
      state: {
        type: String,
        default: null,
        output: true
      },
      card: {
        type: Card,
        default: null
      },
      user: {
        type: User,
        encode: encoders.hasOne,
        reference: true
      },
      issuedVoucherPackage: {
        type: IssuedVoucherPackage,
        default: null,
        output: true
      },
      uid: {
        type: String,
        output: true
      },
      forSelf: {
        type: Boolean,
        local: true
      },
      userId: {
        type: ID,
        local: true
      },
      isReward: {
        type: Boolean,
        local: true,
        default: false
      },
      isManual: {
        type: Boolean,
        local: true,
        default: false
      },
      expiresAt: {
        type: IsoDateTime,
        default: null
      },
      isUnlimited: {
        type: Boolean,
        local: true,
        default: false
      },
      autoCreate: {
        type: Boolean,
        local: true,
        default: false
      },
      redeemedAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },
      createdAt: {
        type: IsoDateTime,
        output: true,
        default: null
      },
      autoApply: {
        type: Boolean,
        local: true,
        default: true
      },
      skipCard: {
        type: Boolean,
        local: true,
        default: false
      },
      validShowingDate: {
        type: IsoDate,
        local: true,
        default: null
      },
      order: {
        type: Order,
        output: true
      },
      membershipVoucher: {
        type: MembershipVoucher,
        default: null,
        local: true
      },
      swapFromOrderItemId: {
        type: ID,
        default: null,
        local: true
      }
    }

    this.computed = {
      description () {
        return this.uid
      },
      requiresCard () {
        return !!this.voucherType?.barcodeOptions?.require_assigned_card && _.isNil(this.card)
      },
      cardNumber () {
        return this.card?.humanUid || this.issuedVoucherPackage?.card?.humanUid
      },
      packageId () {
        return this.issuedVoucherPackage?.id
      },
      packageCode () {
        return this.issuedVoucherPackage?.uid
      },
      voucherPackage () {
        return this.issuedVoucherPackage?.voucherPackage
      },
      stateIcon () {
        if (this.state === VOUCHER_STATES.VALID) {
          return {
            name: 'check',
            color: _.isNil(this.redeemedAt) ? 'positive' : 'grey-4',
            tooltip: this.status
          }
        } else if (this.state === VOUCHER_STATES.VOIDED) {
          return {
            name: 'cancel',
            color: 'negative',
            tooltip: this.status
          }
        } else if (this.state === VOUCHER_STATES.DRAFT) {
          return {
            name: 'info',
            color: 'grey-4',
            tooltip: this.status
          }
        } else if (this.state === VOUCHER_STATES.EXPIRED) {
          return {
            name: 'cancel',
            color: 'grey-4',
            tooltip: this.status
          }
        }

        return null
      },
      status () {
        if (this.state === VOUCHER_STATES.VALID) {
          return _.isNil(this.redeemedAt) ? 'Redeemable' : 'Redeemed'
        } else {
          return _.startCase(this.state)
        }
      }
    }

    this.queries = {
      voucherByUid: {
        arguments: {
          uid: {
            type: String,
            required: true
          }
        },
        results: {
          voucher: Voucher
        }
      }
    }

    this.mutations = {
      voidVoucher: {
        arguments: {
          voucherId: {
            type: ID,
            required: true
          }
        },
        results: {
          voucher: Voucher,
          errors: [GqlError]
        }
      },
      validateVoucher: {
        arguments: {
          voucherId: {
            type: ID,
            required: true
          }
        },
        results: {
          voucher: Voucher,
          errors: [GqlError]
        }
      }
    }

    this.paginate = true
  }

  async voidVoucher () {
    const results = await executeMutation(
      'voidVoucher',
      { voucherId: this.id },
      {
        voucher: {
          structure: {
            id: true,
            state: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return {
      errors: results.errors,
      voucher: results.data?.voucher
    }
  }

  async validateVoucher () {
    const results = await executeMutation(
      'validateVoucher',
      { voucherId: this.id },
      {
        voucher: {
          structure: {
            id: true,
            state: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return {
      errors: results.errors,
      voucher: results.data?.voucher
    }
  }

  get canRemove () {
    return this.isRemote || this.isReward || this.isManual
  }

  get color () {
    return this.isReward ? 'red' : 'primary'
  }

  get label () {
    return this.voucherType.name + (this.isReward ? ` (${this.voucherType.pointsCost})` : '')
  }

  validateCardUid (uid) {
    return this.voucherType.validateCardUid(uid)
  }
}
